import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplyIcon from '@mui/icons-material/Reply';
import SendIcon from '@mui/icons-material/Send';
import UndoIcon from '@mui/icons-material/Undo';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InsightsIcon from '@mui/icons-material/Insights';
import { useAppDispatch } from '../../../app/hooks';
import {
  acceptQcIssue,
  addQcReply,
  deleteQcIssue,
  deleteQcReply,
  markQcDraftRejected,
  replaceQcReply,
  updateQcIssue,
  updateQcReply,
} from '../../../app/store/assessmentWorkspaceSlice';
import {
  QcIssue,
  QcIssueAuthor,
  QcIssueCategory,
  QcIssueStatus,
  QC_ISSUE_CATEGORY_LABELS,
  QC_ISSUE_STATUS_LABELS,
} from '../../../interfaces/qcIssue.interface';
import { QC_CATEGORY_RGB, QC_STATUS_RGB, formatQcDateTime, ACCENT_BLUE, ACCENT_BLUE_DARK, SUCCESS_GREEN, ERROR_RED, WARN_ORANGE, FIRST_PASS_PURPLE } from './qcTheme';
import {
  applyMention,
  getMentionMatches,
  getMentionTerm,
  isSameQcAuthor,
  OrderedQuestionEntry,
  WorkspaceCapabilities,
} from './workspaceDerivations';
import QcAuthorAvatar from './QcAuthorAvatar';
import QuestionMentionMenu from './QuestionMentionMenu';
import LinkedText from './LinkedText';
import QcCategorySelect from './QcCategorySelect';
import {
  AcceptFirstPassIssueModal,
  FirstPassFeedback,
  FirstPassRejection,
  RejectFirstPassIssueModal,
} from './FirstPassIssueModals';
import {
  acceptQcIssueDraft,
  addQcIssueReply,
  deleteQcIssueReply,
  deleteWorkflowQcIssue,
  rejectQcIssueDraft,
  updateQcIssueReply,
  updateWorkflowQcIssue,
} from '../../../services/qcReview';

const Card = styled('div', {
  shouldForwardProp: prop => prop !== 'borderColor',
})<{ borderColor: string }>(({ borderColor }) => ({
  marginTop: '12px',
  border: '1px solid rgba(0,0,0,0.08)',
  borderLeft: `4px solid ${borderColor}`,
  background: '#fff',
  borderRadius: '4px',
  overflow: 'hidden',
  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
  padding: '16px 18px',
}));

const TopRow = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
});

const HeadArea = styled('div')({
  flex: 1,
  minWidth: 0,
});

const HeadRow = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '10px',
});

const NameRow = styled('div')({
  display: 'flex',
  alignItems: 'baseline',
  gap: '8px',
  flexWrap: 'wrap',
});

const AuthorName = styled('span')({
  fontSize: '0.9rem',
  fontWeight: 700,
  color: '#2b2b2b',
});

const AuthorRole = styled('span')({
  fontSize: '0.72rem',
  color: 'rgba(0,0,0,0.5)',
});

const FirstPassInfoIcon = styled(InfoOutlinedIcon)({
  fontSize: '15px',
  color: FIRST_PASS_PURPLE,
  opacity: 0.55,
  cursor: 'default',
  alignSelf: 'center',
});

const CertaintyChip = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  height: '18px',
  padding: '0 8px',
  borderRadius: '9px',
  background: 'rgba(94,53,177,0.08)',
  border: '1px solid rgba(94,53,177,0.3)',
  color: FIRST_PASS_PURPLE,
  fontSize: '0.68rem',
  fontWeight: 700,
  whiteSpace: 'nowrap',
  '& .MuiSvgIcon-root': { fontSize: '12px' },
});

const DateLine = styled('div')({
  fontSize: '0.72rem',
  color: 'rgba(0,0,0,0.45)',
  marginTop: '3px',
});

const AiOriginLine = styled('div')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '5px',
  marginTop: '5px',
  fontSize: '0.7rem',
  color: FIRST_PASS_PURPLE,
});

const RightCluster = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  flex: 'none',
});

const CategoryChip = styled('span', {
  shouldForwardProp: prop => prop !== 'rgb',
})<{ rgb: string }>(({ rgb }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  height: '24px',
  padding: '0 12px',
  borderRadius: '12px',
  fontSize: '0.72rem',
  fontWeight: 600,
  letterSpacing: '0.2px',
  whiteSpace: 'nowrap',
  flex: 'none',
  background: `rgba(${rgb},0.08)`,
  color: `rgb(${rgb})`,
  border: `1px solid rgba(${rgb},0.24)`,
  '&::before': {
    content: '""',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: `rgb(${rgb})`,
    marginRight: '7px',
    flex: 'none',
  },
}));

const MenuAnchor = styled('div')({
  position: 'relative',
  flex: 'none',
});

const KebabButton = styled('button', {
  shouldForwardProp: prop => prop !== 'small',
})<{ small?: boolean }>(({ small }) => ({
  border: 'none',
  background: 'none',
  padding: 0,
  color: 'rgba(0,0,0,0.5)',
  cursor: 'pointer',
  width: small ? '26px' : '28px',
  height: small ? '26px' : '28px',
  borderRadius: '50%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    background: 'rgba(0,0,0,0.06)',
  },
}));

const Menu = styled('div')({
  position: 'absolute',
  top: '32px',
  right: 0,
  zIndex: 20,
  minWidth: '150px',
  background: '#fff',
  border: '1px solid rgba(0,0,0,0.12)',
  borderRadius: '8px',
  boxShadow: '0 4px 16px rgba(0,0,0,0.16)',
  padding: '6px',
});

const MenuItem = styled('button', {
  shouldForwardProp: prop => prop !== 'danger',
})<{ danger?: boolean }>(({ danger }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  width: '100%',
  padding: '9px 10px',
  border: 'none',
  background: 'none',
  borderRadius: '6px',
  fontSize: '0.85rem',
  color: danger ? ERROR_RED : 'rgba(0,0,0,0.8)',
  cursor: 'pointer',
  fontFamily: 'inherit',
  textAlign: 'left',
  '&:hover': {
    background: danger ? 'rgba(198,40,40,0.08)' : 'rgba(0,0,0,0.05)',
  },
}));

const IssueText = styled('div')({
  fontSize: '0.9rem',
  color: 'rgba(0,0,0,0.8)',
  marginTop: '12px',
  lineHeight: 1.55,
});

const EditArea = styled('div')({
  marginTop: '10px',
});

const EditSelectRow = styled('div')({
  marginBottom: '12px',
});

const EditTextarea = styled('textarea', {
  shouldForwardProp: prop => prop !== 'minHeight',
})<{ minHeight: number }>(({ minHeight }) => ({
  width: '100%',
  boxSizing: 'border-box',
  minHeight: `${minHeight}px`,
  border: '1px solid rgba(0,0,0,0.3)',
  borderRadius: '6px',
  padding: '10px 12px',
  fontFamily: 'inherit',
  fontSize: '0.9rem',
  color: 'rgba(0,0,0,0.87)',
  resize: 'vertical',
  outline: 'none',
  background: '#fff',
  '&:focus': {
    borderColor: ACCENT_BLUE,
    boxShadow: '0 0 0 2px rgba(25,118,210,0.18)',
  },
}));

const EditActions = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '8px',
  marginTop: '8px',
});

const SmallTextButton = styled('button')({
  background: '#fff',
  color: 'rgba(0,0,0,0.7)',
  border: 'none',
  borderRadius: '4px',
  padding: '7px 12px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  letterSpacing: '0.46px',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:hover': {
    background: 'rgba(0,0,0,0.04)',
  },
});

const SaveButton = styled('button')({
  background: ACCENT_BLUE,
  color: '#fff',
  border: 'none',
  borderRadius: '4px',
  padding: '7px 16px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  letterSpacing: '0.46px',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:disabled': {
    background: 'rgba(0,0,0,0.12)',
    color: 'rgba(0,0,0,0.4)',
    cursor: 'not-allowed',
  },
});

const RepliesToggle = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  marginTop: '14px',
  border: 'none',
  background: 'none',
  padding: 0,
  fontFamily: 'inherit',
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: ACCENT_BLUE,
  cursor: 'pointer',
  '&:hover': {
    color: ACCENT_BLUE_DARK,
  },
});

const HideRepliesLink = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '5px',
  marginTop: '12px',
  border: 'none',
  background: 'none',
  padding: 0,
  fontFamily: 'inherit',
  fontSize: '0.78rem',
  fontWeight: 600,
  color: 'rgba(0,0,0,0.5)',
  cursor: 'pointer',
  '&:hover': {
    color: ACCENT_BLUE,
  },
});

const ReplyRow = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px',
  marginTop: '14px',
  paddingLeft: '10px',
  borderLeft: '2px solid rgba(25,118,210,0.4)',
});

const ReplyBody = styled('div')({
  flex: 1,
  minWidth: 0,
});

const ReplyHead = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
});

const ReplyMeta = styled('div')({
  display: 'flex',
  alignItems: 'baseline',
  gap: '8px',
  flexWrap: 'wrap',
  minWidth: 0,
});

const ReplyName = styled('span')({
  fontSize: '0.82rem',
  fontWeight: 700,
  color: '#424242',
});

const ReplySubtle = styled('span')({
  fontSize: '0.7rem',
  color: 'rgba(0,0,0,0.5)',
});

const ReplyText = styled('div')({
  fontSize: '0.875rem',
  color: 'rgba(0,0,0,0.87)',
  marginTop: '4px',
});

const ActionBar = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  borderTop: '1px solid rgba(0,0,0,0.07)',
  marginTop: '14px',
  paddingTop: '12px',
  flexWrap: 'wrap',
});

const ReplyLink = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  border: 'none',
  background: 'none',
  padding: 0,
  fontFamily: 'inherit',
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: ACCENT_BLUE,
  cursor: 'pointer',
  '&:hover': {
    color: ACCENT_BLUE_DARK,
  },
});

const ActionButtons = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexWrap: 'wrap',
  justifyContent: 'flex-end',
});

const lifecycleButtonBase = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  border: 'none',
  borderRadius: '6px',
  padding: '8px 14px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  letterSpacing: '0.46px',
  textTransform: 'uppercase' as const,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const ReadyButton = styled('button')({
  ...lifecycleButtonBase,
  background: ACCENT_BLUE,
  color: '#fff',
  '&:hover': {
    background: ACCENT_BLUE_DARK,
  },
});

const ApproveButton = styled('button')({
  ...lifecycleButtonBase,
  background: SUCCESS_GREEN,
  color: '#fff',
  '&:hover': {
    background: '#27682b',
  },
});

const NeedsUpdateButton = styled('button')({
  ...lifecycleButtonBase,
  background: '#fff',
  color: WARN_ORANGE,
  border: '1px solid rgba(230,81,0,0.5)',
  '&:hover': {
    background: 'rgba(230,81,0,0.06)',
  },
});

const RejectDraftButton = styled('button')({
  ...lifecycleButtonBase,
  background: '#fff',
  color: ERROR_RED,
  border: '1px solid rgba(198,40,40,0.5)',
  '&:hover': {
    background: 'rgba(198,40,40,0.06)',
  },
});

const EditDraftButton = styled('button')({
  ...lifecycleButtonBase,
  background: '#fff',
  color: FIRST_PASS_PURPLE,
  border: '1px solid rgba(94,53,177,0.5)',
  '&:hover': {
    background: 'rgba(94,53,177,0.06)',
  },
});

const AcceptDraftButton = styled('button')({
  ...lifecycleButtonBase,
  background: FIRST_PASS_PURPLE,
  color: '#fff',
  '&:hover': {
    background: '#4e2c96',
  },
});

// QC Recheck's advisory box (handoff fpRec spec): dashed, admin-only, shown only on Ready issues.
type RecommendationTone = 'approve' | 'return' | 'unavailable';

const RECOMMENDATION_RGB: Record<RecommendationTone, string> = {
  approve: '46,125,50',
  return: '230,81,0',
  unavailable: '94,53,177',
};

const RecommendationBox = styled('div', {
  shouldForwardProp: prop => prop !== 'tone',
})<{ tone: RecommendationTone }>(({ tone }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  marginTop: '12px',
  padding: '11px 14px',
  borderRadius: '8px',
  background: `rgba(${RECOMMENDATION_RGB[tone]},0.06)`,
  border: `1px dashed rgba(${RECOMMENDATION_RGB[tone]},0.45)`,
}));

const RecommendationTitle = styled('span', {
  shouldForwardProp: prop => prop !== 'tone',
})<{ tone: RecommendationTone }>(({ tone }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '7px',
  fontSize: '0.8rem',
  fontWeight: 700,
  color: `rgb(${RECOMMENDATION_RGB[tone]})`,
  '& .MuiSvgIcon-root': { fontSize: '15px' },
}));

const RecommendationWhy = styled('span')({
  fontSize: '0.8rem',
  color: 'rgba(0,0,0,0.65)',
  lineHeight: 1.5,
});

const StatusMeta = styled('div')({
  fontSize: '0.72rem',
  color: 'rgba(0,0,0,0.5)',
  marginTop: '8px',
});

const ReplyFormArea = styled('div')({
  marginTop: '12px',
});

const ReplyPostButton = styled('button')({
  background: ACCENT_BLUE,
  color: '#fff',
  border: 'none',
  borderRadius: '4px',
  padding: '7px 16px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  letterSpacing: '0.46px',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:disabled': {
    background: 'rgba(0,0,0,0.12)',
    color: 'rgba(0,0,0,0.4)',
    cursor: 'not-allowed',
  },
});

function roleLabel(author: QcIssueAuthor): string {
  return author.role === 'tpn_admin' ? 'TPN Admin' : 'Assessor';
}

// Anonymous admins are named by role; skip the sublabel to avoid "TPN Admin TPN Admin".
function showRoleLabel(author: QcIssueAuthor): boolean {
  return author.name !== roleLabel(author);
}

export interface QcIssueCardProps {
  issue: QcIssue;
  questionId: number;
  entries: OrderedQuestionEntry[];
  capabilities: WorkspaceCapabilities;
  qcReviewSubmitted: boolean;
  currentAuthor: QcIssueAuthor;
  currentRound?: number;
  onSetStatus: (issueId: number, status: QcIssueStatus) => void;
  onNavigateToCode: (shortCode: string) => void;
}

export default function QcIssueCard(props: QcIssueCardProps): JSX.Element {
  const {
    issue, questionId, entries, capabilities, qcReviewSubmitted, currentAuthor, currentRound,
    onSetStatus, onNavigateToCode,
  } = props;
  const dispatch = useAppDispatch();
  const [menuOpen, setMenuOpen] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [editCategory, setEditCategory] = useState<QcIssueCategory>(issue.category);
  const [mentionDismissed, setMentionDismissed] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyDraft, setReplyDraft] = useState('');
  const [replyMentionDismissed, setReplyMentionDismissed] = useState(false);
  const [repliesExpanded, setRepliesExpanded] = useState(false);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const isEditingIssue = editingKey === 'issue';
  // The chip recolors live while the category select changes, before saving.
  const liveCategory = isEditingIssue ? editCategory : issue.category;
  const statusRgb = QC_STATUS_RGB[issue.status];
  const cardBorder = issue.status === 'pending_assessor_action' ? 'rgba(0,0,0,0.2)' : `rgb(${statusRgb})`;

  // Own-issue edits: assessors always; admins only until the QC review is submitted (prototype canEditSelf).
  const canEditIssue = isSameQcAuthor(issue.author, currentAuthor)
    && (currentAuthor.role === 'assessor' || !qcReviewSubmitted);
  const canReady = capabilities.markReadyForTpnReview
    && ['pending_assessor_action', 'needs_update'].includes(issue.status);
  const canDecide = capabilities.approveOrNeedsUpdate && issue.status === 'ready_for_tpn_review';
  // Draft cards are admin-only until submission: accept publishes to the assessor, reject discards without a trace.
  const isFirstPassDraft = issue.status === 'first_pass';
  const canActOnDraft = isFirstPassDraft && capabilities.firstPassVisible && !qcReviewSubmitted;

  const showExpandedReplies = issue.replies.length === 0 || repliesExpanded;
  const mentionTerm = mentionDismissed ? null : getMentionTerm(editDraft);
  const mentionMatches = editingKey !== null && mentionTerm !== null ? getMentionMatches(entries, mentionTerm, 5) : [];
  const replyMentionTerm = replyMentionDismissed ? null : getMentionTerm(replyDraft);
  const replyMentionMatches = replyOpen && replyMentionTerm !== null ? getMentionMatches(entries, replyMentionTerm, 5) : [];

  const startEditIssue = () => {
    setEditingKey('issue');
    setEditDraft(issue.text);
    setEditCategory(issue.category);
    setMentionDismissed(false);
    setMenuOpen(null);
  };

  const saveEditIssue = async () => {
    if (!editDraft.trim()) return;
    const category = editCategory;
    const text = editDraft.trim();
    dispatch(updateQcIssue({ questionId, issueId: issue.id, category, text }));
    setEditingKey(null);
    setEditDraft('');
    if (issue.id > 0) {
      try {
        await updateWorkflowQcIssue(issue.id, category, text);
      } catch (error) {
        console.error('Failed to update QC issue', error);
      }
    }
  };

  const handleDeleteIssue = async () => {
    setMenuOpen(null);
    dispatch(deleteQcIssue({ questionId, issueId: issue.id }));
    if (issue.id > 0) {
      try {
        await deleteWorkflowQcIssue(issue.id);
      } catch (error) {
        console.error('Failed to delete QC issue', error);
      }
    }
  };

  const startEditReply = (replyId: number, text: string) => {
    setEditingKey(`reply-${replyId}`);
    setEditDraft(text);
    setMentionDismissed(false);
    setMenuOpen(null);
  };

  // Optimistic slice update, then the API call; a negative id means the create is in flight — its edits/deletes stay local.
  const saveEditReply = async (replyId: number) => {
    if (!editDraft.trim()) return;
    const text = editDraft.trim();
    dispatch(updateQcReply({ questionId, issueId: issue.id, replyId, text }));
    setEditingKey(null);
    setEditDraft('');
    if (replyId < 0) return;
    try {
      await updateQcIssueReply(replyId, text);
    } catch (error) {
      console.error('Failed to update QC reply', error);
    }
  };

  const handleDeleteReply = async (replyId: number) => {
    dispatch(deleteQcReply({ questionId, issueId: issue.id, replyId }));
    if (replyId < 0) return;
    try {
      await deleteQcIssueReply(replyId);
    } catch (error) {
      console.error('Failed to delete QC reply', error);
    }
  };

  const submitReply = async () => {
    if (!replyDraft.trim()) return;
    const text = replyDraft.trim();
    const tempId = -Date.now();
    dispatch(addQcReply({ questionId, issueId: issue.id, text, author: currentAuthor, id: tempId }));
    setReplyOpen(false);
    setReplyDraft('');
    setRepliesExpanded(true);
    try {
      const created = await addQcIssueReply(issue.id, text);
      dispatch(replaceQcReply({ questionId, issueId: issue.id, replyId: tempId, reply: created }));
    } catch (error) {
      console.error('Failed to post QC reply', error);
    }
  };

  // Optimistic slice update first, then persist; training feedback lives only on the server, so a failed call just logs.
  const commitAcceptDraft = async (feedback: FirstPassFeedback) => {
    dispatch(acceptQcIssue({ questionId, issueId: issue.id, author: currentAuthor }));
    setAcceptOpen(false);
    try {
      await acceptQcIssueDraft(issue.id, feedback);
    } catch (error) {
      console.error('Failed to accept First Pass draft', error);
    }
  };

  const commitRejectDraft = async (rejection: FirstPassRejection) => {
    dispatch(deleteQcIssue({ questionId, issueId: issue.id }));
    dispatch(markQcDraftRejected(questionId));
    setRejectOpen(false);
    try {
      await rejectQcIssueDraft(issue.id, rejection);
    } catch (error) {
      console.error('Failed to reject First Pass draft', error);
    }
  };

  return (
    <Card borderColor={cardBorder}>
      <TopRow>
        <QcAuthorAvatar author={issue.author} size={40} />
        <HeadArea>
          <HeadRow>
            <div style={{ minWidth: 0 }}>
              <NameRow>
                <AuthorName>{issue.author.name}</AuthorName>
                {!isFirstPassDraft && showRoleLabel(issue.author) && <AuthorRole>{roleLabel(issue.author)}</AuthorRole>}
                {isFirstPassDraft && (
                  <>
                    <Tooltip
                      arrow
                      placement='bottom-start'
                      title='Never shown to the assessor. Once accepted, the issue appears as created by TPN Admin.'
                    >
                      <FirstPassInfoIcon aria-label='About First Pass visibility' />
                    </Tooltip>
                    {issue.certainty !== undefined && (
                      <CertaintyChip title='First Pass confidence in this draft'>
                        <InsightsIcon />
                        {Math.round(issue.certainty * 100)}% certain
                      </CertaintyChip>
                    )}
                  </>
                )}
              </NameRow>
              <DateLine>{formatQcDateTime(issue.createdAt)}</DateLine>
              {issue.acceptedFromAi && capabilities.firstPassVisible && (
                <AiOriginLine>
                  <AutoAwesomeIcon sx={{ fontSize: 13 }} />
                  Accepted from First Pass
                </AiOriginLine>
              )}
            </div>
            <RightCluster>
              <CategoryChip rgb={QC_CATEGORY_RGB[liveCategory]}>
                {QC_ISSUE_CATEGORY_LABELS[liveCategory]}
              </CategoryChip>
              {canEditIssue && (
                <MenuAnchor>
                  <KebabButton
                    type='button'
                    aria-label='Issue options'
                    onClick={() => setMenuOpen(prev => (prev === 'issue' ? null : 'issue'))}
                  >
                    <MoreHorizIcon sx={{ fontSize: 20 }} />
                  </KebabButton>
                  {menuOpen === 'issue' && (
                    <Menu>
                      <MenuItem type='button' onClick={startEditIssue}>
                        <EditIcon sx={{ fontSize: 18, color: 'rgba(0,0,0,0.6)' }} />
                        Edit
                      </MenuItem>
                      <MenuItem
                        type='button'
                        danger
                        onClick={handleDeleteIssue}
                      >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                        Delete
                      </MenuItem>
                    </Menu>
                  )}
                </MenuAnchor>
              )}
            </RightCluster>
          </HeadRow>
          {!isEditingIssue && (
            <IssueText>
              <LinkedText text={issue.text} entries={entries} onNavigateToCode={onNavigateToCode} />
            </IssueText>
          )}
          {isEditingIssue && (
            <EditArea>
              <EditSelectRow>
                <QcCategorySelect value={editCategory} height={40} onChange={setEditCategory} />
              </EditSelectRow>
              <EditTextarea
                minHeight={80}
                aria-label='Edit issue'
                value={editDraft}
                onChange={event => { setEditDraft(event.target.value); setMentionDismissed(false); }}
                onKeyDown={event => { if (event.key === 'Escape') setMentionDismissed(true); }}
              />
              <QuestionMentionMenu
                matches={mentionMatches}
                onPick={shortCode => setEditDraft(prev => applyMention(prev, shortCode))}
              />
              <EditActions>
                <SmallTextButton type='button' onClick={() => { setEditingKey(null); setEditDraft(''); }}>
                  Cancel
                </SmallTextButton>
                <SaveButton type='button' disabled={!editDraft.trim()} onClick={saveEditIssue}>Save</SaveButton>
              </EditActions>
            </EditArea>
          )}
          {capabilities.firstPassVisible && issue.status === 'ready_for_tpn_review' && issue.firstPassRecommendation && (() => {
            const { recommendation, comment, roundNumber } = issue.firstPassRecommendation;
            // A prior-round recommendation predates the latest rework — showing it as current misleads the review.
            if (currentRound !== undefined && roundNumber < currentRound) {
              return (
                <RecommendationBox tone='unavailable'>
                  <RecommendationTitle tone='unavailable'>
                    <AutoAwesomeIcon />
                    First Pass re-check pending
                  </RecommendationTitle>
                  <RecommendationWhy>
                    The Round {roundNumber} recommendation predates the latest update. A fresh
                    re-check runs once every open issue is marked Ready for TPN Review.
                  </RecommendationWhy>
                </RecommendationBox>
              );
            }
            const tone: RecommendationTone = recommendation === 'approve' ? 'approve'
              : recommendation === 'needs_update' ? 'return' : 'unavailable';
            return (
              <RecommendationBox tone={tone}>
                <RecommendationTitle tone={tone}>
                  <AutoAwesomeIcon />
                  {recommendation === null
                    ? 'First Pass could not re-evaluate this update'
                    : `First Pass recommends: ${recommendation === 'approve' ? 'Mark Reviewed' : 'Request Update'}`}
                </RecommendationTitle>
                <RecommendationWhy>{comment}</RecommendationWhy>
              </RecommendationBox>
            );
          })()}
          {issue.replies.length > 0 && !repliesExpanded && (
            <RepliesToggle type='button' onClick={() => setRepliesExpanded(true)}>
              {issue.replies.length} replies
            </RepliesToggle>
          )}
          {showExpandedReplies && issue.replies.map(reply => {
            const replyKey = `reply-${reply.id}`;
            const canEditReply = isSameQcAuthor(reply.author, currentAuthor);
            return (
              <ReplyRow key={reply.id}>
                <QcAuthorAvatar author={reply.author} size={30} />
                <ReplyBody>
                  <ReplyHead>
                    <ReplyMeta>
                      <ReplyName>{reply.author.name}</ReplyName>
                      {showRoleLabel(reply.author) && <ReplySubtle>{roleLabel(reply.author)}</ReplySubtle>}
                      <ReplySubtle>{formatQcDateTime(reply.createdAt)}</ReplySubtle>
                    </ReplyMeta>
                    {canEditReply && (
                      <MenuAnchor>
                        <KebabButton
                          type='button'
                          small
                          aria-label='Reply options'
                          onClick={() => setMenuOpen(prev => (prev === replyKey ? null : replyKey))}
                        >
                          <MoreHorizIcon sx={{ fontSize: 18 }} />
                        </KebabButton>
                        {menuOpen === replyKey && (
                          <Menu>
                            <MenuItem type='button' onClick={() => startEditReply(reply.id, reply.text)}>
                              <EditIcon sx={{ fontSize: 18, color: 'rgba(0,0,0,0.6)' }} />
                              Edit
                            </MenuItem>
                            <MenuItem
                              type='button'
                              danger
                              onClick={() => handleDeleteReply(reply.id)}
                            >
                              <DeleteIcon sx={{ fontSize: 18 }} />
                              Delete
                            </MenuItem>
                          </Menu>
                        )}
                      </MenuAnchor>
                    )}
                  </ReplyHead>
                  {editingKey !== replyKey && (
                    <ReplyText>
                      <LinkedText text={reply.text} entries={entries} onNavigateToCode={onNavigateToCode} />
                    </ReplyText>
                  )}
                  {editingKey === replyKey && (
                    <EditArea>
                      <EditTextarea
                        minHeight={64}
                        aria-label='Edit reply'
                        value={editDraft}
                        onChange={event => { setEditDraft(event.target.value); setMentionDismissed(false); }}
                        onKeyDown={event => { if (event.key === 'Escape') setMentionDismissed(true); }}
                      />
                      <QuestionMentionMenu
                        matches={mentionMatches}
                        onPick={shortCode => setEditDraft(prev => applyMention(prev, shortCode))}
                      />
                      <EditActions>
                        <SmallTextButton type='button' onClick={() => { setEditingKey(null); setEditDraft(''); }}>
                          Cancel
                        </SmallTextButton>
                        <SaveButton type='button' disabled={!editDraft.trim()} onClick={() => saveEditReply(reply.id)}>
                          Save
                        </SaveButton>
                      </EditActions>
                    </EditArea>
                  )}
                </ReplyBody>
              </ReplyRow>
            );
          })}
          {issue.replies.length > 0 && repliesExpanded && (
            <HideRepliesLink type='button' onClick={() => setRepliesExpanded(false)}>
              <ExpandLessIcon sx={{ fontSize: 15 }} />
              Hide replies
            </HideRepliesLink>
          )}
          {!replyOpen && (
            <>
              <ActionBar>
                <ReplyLink type='button' onClick={() => { setReplyOpen(true); setReplyDraft(''); setReplyMentionDismissed(false); setRepliesExpanded(true); }}>
                  <ReplyIcon sx={{ fontSize: 16, transform: 'scaleX(-1)' }} />
                  Reply
                </ReplyLink>
                <ActionButtons>
                  {canActOnDraft && (
                    <>
                      <RejectDraftButton type='button' onClick={() => setRejectOpen(true)}>
                        <CloseIcon sx={{ fontSize: 16 }} />
                        Reject
                      </RejectDraftButton>
                      <EditDraftButton type='button' onClick={startEditIssue}>
                        <EditIcon sx={{ fontSize: 16 }} />
                        Edit
                      </EditDraftButton>
                      <AcceptDraftButton type='button' onClick={() => setAcceptOpen(true)}>
                        <CheckIcon sx={{ fontSize: 16 }} />
                        Accept Issue
                      </AcceptDraftButton>
                    </>
                  )}
                  {canReady && (
                    <ReadyButton type='button' onClick={() => onSetStatus(issue.id, 'ready_for_tpn_review')}>
                      <SendIcon sx={{ fontSize: 16 }} />
                      Mark Ready for TPN Review
                    </ReadyButton>
                  )}
                  {canDecide && (
                    <>
                      <NeedsUpdateButton type='button' onClick={() => onSetStatus(issue.id, 'needs_update')}>
                        <UndoIcon sx={{ fontSize: 16 }} />
                        Needs Update
                      </NeedsUpdateButton>
                      <ApproveButton type='button' onClick={() => onSetStatus(issue.id, 'approved')}>
                        <CheckCircleIcon sx={{ fontSize: 16 }} />
                        Mark Reviewed
                      </ApproveButton>
                    </>
                  )}
                </ActionButtons>
              </ActionBar>
              {issue.statusBy && issue.statusAt && (
                <StatusMeta>
                  {QC_ISSUE_STATUS_LABELS[issue.status]} · {issue.statusBy} · {formatQcDateTime(issue.statusAt)}
                </StatusMeta>
              )}
            </>
          )}
          {replyOpen && (
            <ReplyFormArea>
              <EditTextarea
                minHeight={70}
                aria-label='Write a reply'
                placeholder='Write a reply… Type # to link a question.'
                value={replyDraft}
                onChange={event => { setReplyDraft(event.target.value); setReplyMentionDismissed(false); }}
                onKeyDown={event => { if (event.key === 'Escape') setReplyMentionDismissed(true); }}
              />
              <QuestionMentionMenu
                matches={replyMentionMatches}
                onPick={shortCode => { setReplyDraft(prev => applyMention(prev, shortCode)); setReplyMentionDismissed(false); }}
              />
              <EditActions>
                <SmallTextButton type='button' onClick={() => { setReplyOpen(false); setReplyDraft(''); }}>
                  Cancel
                </SmallTextButton>
                <ReplyPostButton type='button' disabled={!replyDraft.trim()} onClick={submitReply}>Reply</ReplyPostButton>
              </EditActions>
            </ReplyFormArea>
          )}
        </HeadArea>
      </TopRow>
      {canActOnDraft && (
        <>
          <AcceptFirstPassIssueModal
            open={acceptOpen}
            issue={issue}
            onAccept={commitAcceptDraft}
            onClose={() => setAcceptOpen(false)}
          />
          <RejectFirstPassIssueModal
            open={rejectOpen}
            issue={issue}
            onReject={commitRejectDraft}
            onClose={() => setRejectOpen(false)}
          />
        </>
      )}
    </Card>
  );
}
